import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPrograms } from '../services/api';
import { GraduationCap, ArrowRight, Zap, Calendar, FileEdit, Mic, MessageCircle, BookOpen, ShieldCheck } from 'lucide-react';

const ProgramsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [VAE_PROGRAMS, setPrograms] = useState([]);
  const navigate = useNavigate();

  const DIPLOMA_LEVELS = {
    'cap-aepe': { level: 'Niveau 3', label: 'CAP', rncp: 'RNCP 38565', certif: 'Certificateur Ministère de l\'Éducation Nationale', date: '22/01/2024' },
    'deap': { level: 'Niveau 4', label: 'Diplôme d\'État', rncp: 'RNCP 40743', certif: 'Certificateur Dreets', date: '12/06/2021' },
    'deeje': { level: 'Niveau 6', label: 'Grade Licence', rncp: 'RNCP 37679', certif: 'Certificateur Dreets', date: '09/04/2020' },
    'caferuis': { level: 'Niveau 6', label: 'Encadrement', rncp: 'RNCP 36836', certif: 'Certificateur Dreets, Ministère de la Solidarité', date: '31/08/2022' },
    'tp': { level: 'Niveau 4', label: 'Titre Professionnel', rncp: 'RNCP 41849', certif: 'Certificateur Ministère chargé de l\'Emploi', date: '15/12/2025' },
    'bac-assp': { level: 'Niveau 4', label: 'Baccalauréat', rncp: 'RNCP 37231', certif: 'Certificateur Dava, Ministère de l\'Éducation nationale', date: '04/03/2022' },
    'deas': { level: 'Niveau 4', label: 'Diplôme d\'État', rncp: 'RNCP 40692', certif: 'Certificateur Dreets', date: '12/06/2021' }
  };

  useEffect(() => {
    fetchPrograms().then(setPrograms).catch(console.error);
  }, []);

  const categories = [
    { id: 'all', name: 'Tous' },
    { id: 'petite-enfance', name: 'Petite Enfance', programs: ['cap-aepe', 'deap'] },
    { id: 'education', name: 'Éducation', programs: ['deeje'] },
    { id: 'social', name: 'Encadrement Social', programs: ['caferuis'] },
    { id: 'professionnel', name: 'Titre Professionnel', programs: ['tp'] },
    { id: 'urgence-vae', name: 'Urgence VAE' }
  ];

  const STANDALONE_IDS = ['oral-vae', 'livret-1-asp', 'veille-professionnelle', 'veille-pro'];
  const filteredPrograms = (selectedCategory === 'all' 
    ? VAE_PROGRAMS 
    : VAE_PROGRAMS.filter(p => {
        const category = categories.find(c => c.id === selectedCategory);
        return category && category.programs && category.programs.includes(p.id);
      })
  ).filter(p => !STANDALONE_IDS.includes(p.id));

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--gradient-hero-warm)',
        padding: '5rem 1.2rem 3rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            Nos Formations VAE
          </h1>
          <p className="body-large">
            Trouvez la formation qui correspond à votre expérience professionnelle 
            et commencez votre parcours de validation
          </p>
        </div>
      </section>

      {/* Filters */}
      <section style={{ padding: '2rem 1.2rem' }}>
        <div className="container">
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '3rem'
          }}>
            {categories.map((category) => (
              category.id === 'urgence-vae' ? (
                <button
                  key={category.id}
                  onClick={() => {
                    document.getElementById('urgence-vae-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    padding: '0.6rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #F97316 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 2px 12px rgba(249, 115, 22, 0.4)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    position: 'relative',
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(249, 115, 22, 0.55)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(249, 115, 22, 0.4)'; }}
                  data-testid="urgence-vae-tab"
                >
                  <span style={{
                    display: 'inline-block',
                    animation: 'urgenceStar 1s ease-in-out infinite'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </span>
                  Urgence VAE
                  <span style={{
                    display: 'inline-block',
                    animation: 'urgenceStar 1s ease-in-out infinite',
                    animationDelay: '0.5s'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </span>
                  <style>{`
                    @keyframes urgenceStar {
                      0%, 100% { opacity: 1; transform: scale(1); }
                      50% { opacity: 0.3; transform: scale(0.6); }
                    }
                  `}</style>
                </button>
              ) : (
              <button
                key={category.id}
                onClick={() => {
                    setSelectedCategory(category.id);
                }}
                className={selectedCategory === category.id ? 'btn-primary' : 'btn-secondary'}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.75rem'
                }}
              >
                {category.name}
              </button>
              )
            ))}
          </div>

          {/* Programs Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
            gap: '2rem'
          }}>
            {filteredPrograms.map((program) => {
              const diplomaInfo = DIPLOMA_LEVELS[program.id] || { level: '—', label: '' };
              return (
                <div 
                  key={program.id}
                  className={`card accent-${program.color} hover-lift`}
                  style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => navigate(`/programs/${program.id}`)}
                  data-testid={`program-card-${program.id}`}
                >
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.6)',
                      borderRadius: '1rem',
                      fontSize: '0.7rem',
                      fontFamily: 'var(--font-mono)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {program.title}
                    </span>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(0,0,0,0.06)',
                      borderRadius: '1rem',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}>
                      <GraduationCap size={14} />
                      {diplomaInfo.level}
                    </span>
                  </div>

                  <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>
                    {program.fullName}
                  </h3>

                  <p className="body-small" style={{ 
                    marginBottom: '1rem',
                    flex: 1,
                    color: 'var(--text-secondary)'
                  }}>
                    {program.description}
                  </p>

                  {diplomaInfo.rncp && (
                    <div style={{
                      background: 'rgba(0,0,0,0.03)',
                      borderRadius: '8px',
                      padding: '0.5rem 0.75rem',
                      marginBottom: '1rem',
                      fontSize: '0.7rem',
                      color: 'var(--text-muted)',
                      lineHeight: 1.5
                    }}>
                      <strong style={{ color: 'var(--text-secondary)' }}>{diplomaInfo.rncp}</strong> · {diplomaInfo.certif}<br />
                      Enregistrement au RNCP : {diplomaInfo.date}
                    </div>
                  )}

                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--border-light)'
                  }}>
                    <button 
                      className="btn-primary"
                      style={{ 
                        padding: '0.5rem 1.25rem',
                        fontSize: '0.75rem',
                        gap: '0.5rem'
                      }}
                      data-testid={`view-details-${program.id}`}
                    >
                      <span>Voir détails</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredPrograms.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem',
              color: 'var(--text-muted)'
            }}>
              <p className="body-large">Aucune formation trouvée pour cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* Urgence VAE Section */}
      <section id="urgence-vae-section" style={{
        padding: '4rem 1.2rem',
        background: 'linear-gradient(135deg, #FFF1F2 0%, #FFF7ED 100%)'
      }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{
              background: '#F97316', borderRadius: '50%', width: '48px', height: '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <Zap size={24} color="white" />
            </div>
            <h2 className="heading-1">Urgence VAE</h2>
          </div>
          <div style={{ marginBottom: '2rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <p className="body-large" style={{ marginBottom: '0.5rem' }}>
              La date limite de dépôt du livret 2 approche ?
            </p>
            <p className="body-large" style={{ marginBottom: '0.5rem' }}>
              Vous avez reçu votre convocation au jury et le stress monte ?
            </p>
            <p className="body-large" style={{ marginBottom: '1rem' }}>
              Vous avez besoin d'une aide rapide pour finaliser votre dossier ou préparer votre oral ?
            </p>
            <p className="body-large" style={{ fontWeight: 700, color: '#E75480' }}>
              Choisissez la formule d'accompagnement la plus adaptée à votre besoin.
            </p>
          </div>

          <div className="grid-responsive-2">

            {/* Urgence Dépôt Livret 2 */}
            <div style={{
              background: 'white', borderRadius: '1.25rem', overflow: 'hidden',
              border: '2px solid #F97316',
              boxShadow: '0 4px 20px rgba(249, 115, 22, 0.12)',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                padding: '1.5rem 1.75rem', color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <FileEdit size={24} />
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Urgence Dépôt Livret 2</h3>
                </div>
                <p style={{ opacity: 0.9, fontSize: '0.85rem' }}>Finalisez votre Livret 2 avant la date limite</p>
              </div>
              <div style={{ padding: '1.5rem 1.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.5rem', flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <Calendar size={17} style={{ color: '#F97316', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Séances intensives</strong> adaptées à votre deadline</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <FileEdit size={17} style={{ color: '#3B82F6', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Relecture express</strong> et correction rapide de votre Livret 2</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <Calendar size={17} style={{ color: '#9b59b6', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Créneaux spéciaux urgence</strong> ouverts en priorité</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <BookOpen size={17} style={{ color: '#10B981', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Accès à la plateforme</strong> de formation en ligne</span>
                  </div>
                </div>
                <div style={{
                  textAlign: 'center', padding: '1.25rem',
                  background: '#FFF7ED', borderRadius: '0.75rem', marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: 800, color: '#F97316' }}>490 €</div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => navigate('/booking')}>
                    Réserver
                  </button>
                  <a href="mailto:apdr.vae@gmail.com" className="btn-secondary" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                    Nous contacter
                  </a>
                </div>
              </div>
            </div>

            {/* Urgence Oral */}
            <div style={{
              background: 'white', borderRadius: '1.25rem', overflow: 'hidden',
              border: '2px solid #DC2626',
              boxShadow: '0 4px 20px rgba(220, 38, 38, 0.12)',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                padding: '1.5rem 1.75rem', color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <Mic size={24} />
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Urgence Oral</h3>
                </div>
                <p style={{ opacity: 0.9, fontSize: '0.85rem' }}>Préparez votre passage devant le jury en un temps record</p>
              </div>
              <div style={{ padding: '1.5rem 1.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.5rem', flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <FileEdit size={17} style={{ color: '#DC2626', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Lecture de votre Livret 2</strong> et retour détaillé</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <MessageCircle size={17} style={{ color: '#3B82F6', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>2 séances de 30 min</strong> de travail sur la présentation personnelle</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <Mic size={17} style={{ color: '#9b59b6', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>2 oraux blancs d'1 heure</strong> avec débrief complet</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <ShieldCheck size={17} style={{ color: '#10B981', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Support de questions type</strong> pour travailler en autonomie</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <BookOpen size={17} style={{ color: '#6366F1', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.88rem' }}><strong>Accès à la plateforme</strong> en ligne pour vos révisions</span>
                  </div>
                </div>
                <div style={{
                  textAlign: 'center', padding: '1.25rem',
                  background: '#FEF2F2', borderRadius: '0.75rem', marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: 800, color: '#DC2626' }}>590 €</div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ flex: 1, justifyContent: 'center', background: '#DC2626' }} onClick={() => navigate('/booking')}>
                    Réserver
                  </button>
                  <a href="mailto:apdr.vae@gmail.com" className="btn-secondary" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                    Nous contacter
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Information Section */}
      <section style={{ 
        padding: '5rem 1.2rem',
        background: 'var(--bg-section)'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Comment fonctionne notre accompagnement VAE ?
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ 
                  background: 'var(--accent-purple-200)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 600
                }}>
                  1
                </div>
                <div style={{ flex: 1 }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    Choisissez votre formule
                  </h3>
                  <p className="body-small">
                    Sélectionnez la formule adaptée à votre besoin : <strong>Bronze</strong> (formation en ligne en autonomie), <strong>Silver</strong> (accompagnement avec séances de coaching, corrections et oral blanc) ou <strong>Gold</strong> (accompagnement illimité pendant 1 an).
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ 
                  background: 'var(--accent-blue-200)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 600
                }}>
                  2
                </div>
                <div style={{ flex: 1 }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    Accédez à la plateforme de formation
                  </h3>
                  <p className="body-small">
                    Dès votre inscription, accédez à notre plateforme en ligne avec des cours, des quiz et des supports pédagogiques conçus pour vous aider à rédiger vos livrets 1 et 2.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ 
                  background: 'var(--accent-orange-200)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 600
                }}>
                  3
                </div>
                <div style={{ flex: 1 }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    Bénéficiez de l'accompagnement (Silver & Gold)
                  </h3>
                  <p className="body-small">
                    Participez à des séances de coaching individuelles en ligne, recevez des corrections approfondies de votre Livret 2, des appels explicatifs et préparez votre oral avec des simulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ 
                  background: 'var(--accent-green-200)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 600
                }}>
                  4
                </div>
                <div style={{ flex: 1 }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    Passage devant le jury
                  </h3>
                  <p className="body-small">
                    Vous passez devant le jury en toute confiance grâce à votre préparation. Votre accompagnatrice vous aura donné toutes les clés pour réussir votre certification !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
